/* eslint-disable max-lines */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from '@/lib/query-keys';
import { callEdgeFunction, callEdgeFunctionJson } from '@/lib/edge-function-client';
import {
  clearOAuthState,
  getTokenErrorMessage,
  isReauthRequired,
  storeOAuthState,
  validateOAuthState,
} from './useClickUp.oauth';
import type {
  ClickUpConnection,
  ClickUpList,
  ClickUpMember,
  ClickUpSpace,
  ClickUpWorkspace,
  OAuthStateData,
  PushedTask,
  TokenErrorResponse,
} from './useClickUp.types';
import { useClickUpConnection } from './useClickUpAuth';
export {
  useClickUpConnection,
  useClickUpDisconnect,
  useClickUpGenerateOAuthUrl,
  useClickUpOAuthExchange,
  useClickUpRefreshToken,
  useClickUpTokenStatus,
} from './useClickUpAuth';
export type {
  ClickUpConnection,
  ClickUpList,
  ClickUpMember,
  ClickUpSpace,
  ClickUpWorkspace,
  OAuthStateData,
  PushedTask,
  TokenErrorCode,
  TokenErrorResponse,
} from './useClickUp.types';
export {
  clearOAuthState,
  getClickUpOAuthUrl,
  getStoredOAuthState,
  getTokenErrorMessage,
  isReauthRequired,
  storeOAuthState,
  validateOAuthState,
} from './useClickUp.oauth';

// Hook to get ClickUp workspaces
export function useClickUpWorkspaces() {
  const { data: connection } = useClickUpConnection();

  return useQuery({
    queryKey: queryKeys.clickup.workspaces,
    queryFn: async () => {
      const response = await supabase.functions.invoke('clickup-oauth', {
        body: { action: 'workspaces' },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch workspaces');
      }

      return response.data.workspaces as ClickUpWorkspace[];
    },
    enabled: !!connection,
  });
}

// Hook to get ClickUp spaces
export function useClickUpSpaces(workspaceId: string | null) {
  const { data: connection } = useClickUpConnection();

  return useQuery({
    queryKey: queryKeys.clickup.spaces(workspaceId || ''),
    queryFn: async () => {
      if (!workspaceId) return [];

      const response = await supabase.functions.invoke('clickup-oauth', {
        body: { action: 'spaces', workspace_id: workspaceId },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch spaces');
      }

      return response.data.spaces as ClickUpSpace[];
    },
    enabled: !!connection && !!workspaceId,
  });
}

// Hook to get ClickUp lists
export function useClickUpLists(spaceId: string | null) {
  const { data: connection } = useClickUpConnection();

  return useQuery({
    queryKey: queryKeys.clickup.lists(spaceId || ''),
    queryFn: async () => {
      if (!spaceId) return [];

      const response = await supabase.functions.invoke('clickup-oauth', {
        body: { action: 'lists', space_id: spaceId },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch lists');
      }

      return response.data.lists as ClickUpList[];
    },
    enabled: !!connection && !!spaceId,
  });
}

// Hook to get ClickUp team members
export function useClickUpTeamMembers() {
  const { data: connection } = useClickUpConnection();

  return useQuery({
    queryKey: queryKeys.clickup.teamMembers,
    queryFn: async () => {
      const response = await supabase.functions.invoke('clickup-oauth', {
        body: { action: 'team-members' },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch team members');
      }

      return response.data.members as ClickUpMember[];
    },
    enabled: !!connection,
  });
}

// Hook to save default workspace/space/list
export function useClickUpSaveDefaults() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (defaults: {
      workspace_id: string;
      workspace_name: string;
      space_id: string;
      space_name: string;
      list_id: string;
      list_name: string;
    }) => {
      const response = await supabase.functions.invoke('clickup-oauth', {
        body: { action: 'save-defaults', ...defaults },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to save defaults');
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clickup.connection });
      toast({ title: 'Default list saved!' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to save defaults',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Hook to push a task to ClickUp
export function usePushTaskToClickUp() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (task: {
      name: string;
      description?: string;
      priority?: string;
      start_date?: string;
      due_date?: string;
      time_estimate?: string; // e.g., "1h", "30m", "2h"
      status?: string;
      assignees?: number[];
      list_id?: string;
      parent_task_id?: string; // For creating subtasks under an existing ClickUp task
    }) => {
      console.log('Pushing task to ClickUp:', JSON.stringify(task, null, 2));

      const response = await callEdgeFunction<{ task?: { name?: string } }>(
        'clickup-tasks/create',
        {
          method: 'POST',
          body: task,
          auth: 'session',
        },
      );

      console.log('ClickUp response:', response.status, response.data);
      return response.data;
    },
    onSuccess: (data) => {
      toast({
        title: 'Task created in ClickUp!',
        description: data.task?.name,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create task',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Hook to push multiple tasks to ClickUp
export function usePushTasksToClickUp() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      tasks: Array<{
        index: number;
        name: string;
        title?: string;
        description?: string;
        priority?: string;
        start_date?: string;
        due_date?: string;
        suggested_start_date?: string;
        suggested_due_date?: string;
        time_estimate?: string; // e.g., "1h", "30m", "2h"
        status?: string;
        assignees?: number[];
        parent_task_id?: string; // For creating subtasks under an existing ClickUp task
      }>;
      list_id?: string;
    }) => {
      console.log('Pushing tasks to ClickUp:', params);
      const response = await callEdgeFunctionJson<{
        created: number;
        total: number;
        failed: number;
        results?: Array<{ was_reparented?: boolean }>;
      }>('clickup-tasks/create-bulk', {
        method: 'POST',
        body: params,
        auth: 'session',
      });

      return response;
    },
    onSuccess: (data) => {
      toast({
        title: `Created ${data.created} of ${data.total} tasks in ClickUp`,
        variant: data.failed > 0 ? 'destructive' : 'default',
      });

      // Show re-parenting toast if any tasks were re-parented
      const reparentedCount = data.results?.filter((r) => r.was_reparented).length ?? 0;
      if (reparentedCount > 0) {
        toast({
          title: `${reparentedCount} task(s) were automatically re-parented`,
          description: 'Tasks were moved to respect the depth limit (max 4 levels).',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create tasks',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Helper to check if a task has been pushed
export function isTaskPushed(
  pushedTasks: PushedTask[] | null | undefined,
  taskIndex: number,
  taskType: 'new_task' | 'related_subtask',
): PushedTask | undefined {
  if (!pushedTasks || !Array.isArray(pushedTasks)) return undefined;
  return pushedTasks.find((pt) => pt.task_index === taskIndex && pt.task_type === taskType);
}

export interface ClickUpStatus {
  name: string;
  color: string;
  type: string; // "open", "custom", "closed"
  orderindex: number;
}

// Hook to get ClickUp statuses from list (preferred) or space (fallback)
export function useClickUpStatuses(spaceId?: string | null, listId?: string | null) {
  const { data: connection } = useClickUpConnection();

  // Use the provided listId, or fall back to connection's default_list_id
  const effectiveListId = listId || connection?.default_list_id;
  const effectiveSpaceId = spaceId || connection?.default_space_id;

  return useQuery({
    queryKey: queryKeys.clickup.statuses(effectiveSpaceId, effectiveListId),
    queryFn: async () => {
      const response = await supabase.functions.invoke('clickup-oauth', {
        body: {
          action: 'statuses',
          space_id: effectiveSpaceId,
          list_id: effectiveListId,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch statuses');
      }

      return response.data.statuses as ClickUpStatus[];
    },
    enabled: !!connection && (!!effectiveSpaceId || !!effectiveListId),
  });
}

// ClickUp list member interface
export interface ClickUpListMember {
  id: string;
  username: string;
  email: string;
  color?: string;
  initials?: string;
  profilePicture?: string;
}

// Hook to get members who have access to a ClickUp list
export function useClickUpListMembers(listId?: string | null) {
  const { data: connection } = useClickUpConnection();

  return useQuery({
    queryKey: queryKeys.clickup.listMembers(listId),
    queryFn: async () => {
      const response = await supabase.functions.invoke('clickup-oauth', {
        body: {
          action: 'list-members',
          list_id: listId,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch list members');
      }

      return response.data.members as ClickUpListMember[];
    },
    enabled: !!connection && !!listId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

// Hook to sync list members to database for visibility control
export function useSyncListMembers() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (listId: string) => {
      // First fetch members from ClickUp
      const fetchResponse = await supabase.functions.invoke('clickup-oauth', {
        body: {
          action: 'list-members',
          list_id: listId,
        },
      });

      if (fetchResponse.error) {
        throw new Error(fetchResponse.error.message || 'Failed to fetch list members');
      }

      const members = fetchResponse.data.members as ClickUpListMember[];

      // Delete existing members for this list
      const { error: deleteError } = await supabase
        .from('clickup_list_members')
        .delete()
        .eq('clickup_list_id', listId);

      if (deleteError) {
        console.error('Error deleting old list members:', deleteError);
      }

      // Insert new members
      if (members.length > 0) {
        const { error: insertError } = await supabase.from('clickup_list_members').insert(
          members.map((m) => ({
            clickup_list_id: listId,
            clickup_user_id: m.id,
            username: m.username,
            email: m.email,
            profile_picture: m.profilePicture,
            last_synced_at: new Date().toISOString(),
          })),
        );

        if (insertError) {
          throw new Error('Failed to sync list members: ' + insertError.message);
        }
      }

      return { synced: members.length, list_id: listId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clickup.listMembers(data.list_id) });
      // Also invalidate accessible client IDs since membership changed
      queryClient.invalidateQueries({ queryKey: queryKeys.clickup.accessibleClients });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      toast({
        title: `Synced ${data.synced} list members`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to sync list members',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Hook to sync list members for ALL clients at once
export function useSyncAllListMembers() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      // Get all clients with ClickUp lists
      const { data: clients, error: clientsError } = await supabase
        .from('clients')
        .select('id, name, clickup_list_id')
        .not('clickup_list_id', 'is', null);

      if (clientsError) throw clientsError;
      if (!clients || clients.length === 0) {
        return { synced: 0, total: 0, errors: [] };
      }

      const errors: string[] = [];
      let totalSynced = 0;

      // Sync each client's list members
      for (const client of clients) {
        try {
          // Fetch members from ClickUp
          const fetchResponse = await supabase.functions.invoke('clickup-oauth', {
            body: {
              action: 'list-members',
              list_id: client.clickup_list_id,
            },
          });

          if (fetchResponse.error) {
            errors.push(`${client.name}: ${fetchResponse.error.message}`);
            continue;
          }

          const members = fetchResponse.data.members || [];

          // Delete existing members for this list
          await supabase
            .from('clickup_list_members')
            .delete()
            .eq('clickup_list_id', client.clickup_list_id);

          // Insert new members
          if (members.length > 0) {
            await supabase.from('clickup_list_members').insert(
              members.map((m) => ({
                clickup_list_id: client.clickup_list_id,
                clickup_user_id: m.id,
                username: m.username,
                email: m.email,
                profile_picture: m.profilePicture,
                last_synced_at: new Date().toISOString(),
              })),
            );
          }

          totalSynced += members.length;
        } catch (err) {
          errors.push(`${client.name}: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      }

      return { synced: totalSynced, total: clients.length, errors };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clickup.listMembers(null) });
      queryClient.invalidateQueries({ queryKey: queryKeys.clickup.accessibleClients });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });

      if (data.errors.length > 0) {
        toast({
          title: `Synced ${data.synced} members from ${data.total - data.errors.length}/${data.total} clients`,
          description: `${data.errors.length} clients had errors`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: `Synced ${data.synced} members from ${data.total} clients`,
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to sync list members',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Hook to sync team members from ClickUp list members to org_people and client_team_members
export function useSyncTeamMembersFromClickUp() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ clientId, listId }: { clientId: string; listId: string }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch list members from ClickUp
      const fetchResponse = await supabase.functions.invoke('clickup-oauth', {
        body: {
          action: 'list-members',
          list_id: listId,
        },
      });

      if (fetchResponse.error) {
        throw new Error(fetchResponse.error.message || 'Failed to fetch list members');
      }

      const members = fetchResponse.data.members as ClickUpListMember[];
      let addedToOrg = 0;
      let addedToClient = 0;

      for (const member of members) {
        const clickupUserId = String(member.id);

        // Check if person exists in org_people by clickup_user_id
        let { data: existingPerson } = await supabase
          .from('org_people')
          .select('*')
          .eq('user_id', user.id)
          .eq('clickup_user_id', clickupUserId)
          .maybeSingle();

        // If not found by clickup_user_id, try by email
        if (!existingPerson && member.email) {
          const { data: personByEmail } = await supabase
            .from('org_people')
            .select('*')
            .eq('user_id', user.id)
            .eq('email', member.email)
            .maybeSingle();

          if (personByEmail) {
            // Update with ClickUp info
            await supabase
              .from('org_people')
              .update({
                clickup_user_id: clickupUserId,
                clickup_username: member.username,
              })
              .eq('id', personByEmail.id);
            existingPerson = personByEmail;
          }
        }

        // Create in org_people if not exists
        if (!existingPerson) {
          const { data: newPerson, error: insertError } = await supabase
            .from('org_people')
            .insert({
              user_id: user.id,
              name: member.username || member.email || `User ${clickupUserId}`,
              email: member.email || null,
              role_title: 'Team Member',
              side_type: 'agency_internal',
              clickup_user_id: clickupUserId,
              clickup_username: member.username,
              is_active: true,
            })
            .select()
            .single();

          if (insertError) {
            console.error('Failed to create org person:', insertError);
            continue;
          }
          existingPerson = newPerson;
          addedToOrg++;
        }

        // Check if already on this client's team
        const { data: existingTeamMember } = await supabase
          .from('client_team_members')
          .select('id')
          .eq('client_id', clientId)
          .eq('org_person_id', existingPerson.id)
          .maybeSingle();

        // Add to client_team_members if not already there
        if (!existingTeamMember) {
          const { error: teamError } = await supabase.from('client_team_members').insert({
            client_id: clientId,
            org_person_id: existingPerson.id,
            name: existingPerson.name,
            email: existingPerson.email,
            role_title: existingPerson.role_title,
            side_type: existingPerson.side_type,
            is_active_on_client: true,
          });

          if (teamError) {
            console.error('Failed to add team member to client:', teamError);
          } else {
            addedToClient++;
          }
        }
      }

      return { addedToOrg, addedToClient, total: members.length };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.people });
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.teamMembers(variables.clientId),
      });

      if (data.addedToClient > 0 || data.addedToOrg > 0) {
        toast({
          title: 'Team members synced from ClickUp',
          description: `Added ${data.addedToClient} to client team${data.addedToOrg > 0 ? `, ${data.addedToOrg} new to org directory` : ''}`,
        });
      }
    },
    onError: (error: Error) => {
      console.error('Failed to sync team members:', error);
      // Don't show error toast - this is a secondary operation
    },
  });
}

/**
 * Pushes field updates (description, dates, priority, name) to an existing ClickUp task.
 * Also optionally pushes status changes and adds a comment.
 * @param params.clickupTaskId - The ClickUp task ID to update
 * @param params.updates - Field updates to push (description, due_date, start_date, priority, name, time_estimate, status)
 * @param params.comment - Optional comment text to add to the task
 * @returns Results object with details, status, and comment sub-results
 */
export function usePushTaskUpdate() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      clickupTaskId: string;
      updates: {
        description?: string;
        due_date?: string;
        start_date?: string;
        priority?: string;
        name?: string;
        time_estimate?: string;
        status?: string;
      };
      comment?: string;
    }) => {
      const results: { details?: unknown; status?: unknown; comment?: unknown } = {};

      // Separate status from other field updates (status uses existing push-status endpoint)
      const { status, ...fieldUpdates } = params.updates;

      if (status) {
        results.status = await callEdgeFunctionJson('clickup-sync/push-status', {
          body: { clickup_task_id: params.clickupTaskId, new_status: status },
        });
      }

      // Push other field updates via push-details
      if (Object.keys(fieldUpdates).length > 0) {
        results.details = await callEdgeFunctionJson('clickup-sync/push-details', {
          body: { clickup_task_id: params.clickupTaskId, updates: fieldUpdates },
        });
      }

      // Add comment if provided
      if (params.comment) {
        results.comment = await callEdgeFunctionJson('clickup-tasks/comment', {
          body: { task_id: params.clickupTaskId, comment: params.comment },
        });
      }

      return results;
    },
    onSuccess: () => {
      toast({ title: 'Task updated in ClickUp' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update task',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Hook to check if current user has access to a client via ClickUp list
export function useClientAccess(clientId?: string | null) {
  return useQuery({
    queryKey: queryKeys.clickup.clientAccess(clientId || ''),
    queryFn: async () => {
      if (!clientId) return { hasAccess: true };

      const { data, error } = await supabase.rpc('user_has_client_access', {
        check_user_id: (await supabase.auth.getUser()).data.user?.id,
        check_client_id: clientId,
      });

      if (error) {
        console.error('Error checking client access:', error);
        return { hasAccess: true }; // Default to allowing access on error
      }

      return { hasAccess: data as boolean };
    },
    enabled: !!clientId,
  });
}
